import { defineStore } from "pinia"
import { type Company, type OrganizationChartNode } from "~/models/company"
import { type ApiResponse } from "~/types/api"

export const useCompanyStore = defineStore("company", () => {
	const companyId = useCookie<number | null>("company_id", {
		maxAge: 60 * 60 * 24 * 30 * 2
	})
	const state = {
		companyId: ref(companyId.value),
		company: ref<Company | null>(null),
		payrollDomainCompany: ref<Company | null>(null),
		careerDomainCompany: ref<Company | null>(null)
	}

	const domainCompany = computed(() => {
		if (state.payrollDomainCompany.value) {
			return {
				...state.payrollDomainCompany.value,
				domain: state.payrollDomainCompany.value.payroll_domain,
				homepage: state.payrollDomainCompany.value.payroll_homepage,
				baseurl: state.payrollDomainCompany.value.payroll_baseurl,
				is_domain_ok: state.payrollDomainCompany.value.is_payroll_domain_ok,
				is_change_domain: state.payrollDomainCompany.value.is_change_payroll_domain
			}
		} else if (state.careerDomainCompany.value) {
			return {
				...state.careerDomainCompany.value,
				domain: state.careerDomainCompany.value.career_domain,
				homepage: state.careerDomainCompany.value.career_homepage,
				baseurl: state.careerDomainCompany.value.career_baseurl,
				is_domain_ok: state.careerDomainCompany.value.is_career_domain_ok,
				is_change_domain: state.careerDomainCompany.value.is_change_career_domain
			}
		} else {
			return null
		}
	})

	const setCompany = (id: number | null, company?: Company | null) => {
		companyId.value = id
		state.companyId.value = id
		if (company !== undefined) state.company.value = company ? { ...company } : null
	}

	const fetchCompany = async (force: boolean = false) => {
		const userStore = useUserStore()
		if (state.companyId.value == state.company.value?.id && !force) return
		try {
			if (state.companyId.value) {
				const c = await getCompany(state.companyId.value)
				if (
					!["admin", "support"].includes(userStore.user?.type || "") &&
					c.user_id != userStore.user?.id &&
					c.id != userStore.user?.company_id
				) {
					state.companyId.value = null
					state.company.value = null
				} else {
					state.company.value = c
				}
			} else {
				state.company.value = null
			}
		} catch (e: any) {
			if (e.response._data?.errors?.id?.exist) {
				state.companyId.value = null
				state.company.value = null
			} else {
				throw e
			}
		}
	}

	const createCompany = async (formData: CreateCompanyRequest) => {
		const data = await $fetch<CreateCompanyResponse>(CreateCompany.url(), {
			...useFetchConfig(),
			method: CreateCompany.method,
			body: formData
		})
		return data.data
	}

	const editCompany = async (formData: EditCompanyRequest) => {
		const data = await $fetch<EditCompanyResponse>(EditCompany.url(), {
			...useFetchConfig(),
			method: EditCompany.method,
			body: formData
		})
		if (state.company?.value?.id == data?.data?.id) {
			state.company.value = data.data
		}
		return data.data
	}

	const deleteCompany = async (id: number) => {
		const data = await $fetch<DeleteCompanyResponse>(DeleteCompany.url(id), {
			...useFetchConfig(),
			method: DeleteCompany.method
		})
		return data.data
	}

	const getAllCompanies = async (formData: GetAllCompaniesRequest) => {
		const data = await $fetch<GetAllCompaniesResponse>(GetAllCompanies.url(), {
			...useFetchConfig(),
			method: GetAllCompanies.method,
			query: formData
		})
		return data.data
	}

	const searchCompanies = async (formData: SearchCompaniesRequest) => {
		const data = await $fetch<SearchCompaniesResponse>(SearchCompanies.url(), {
			...useFetchConfig(),
			method: SearchCompanies.method,
			query: formData
		})
		return data.data
	}

	const checkCName = async (formData: CompanyCheckCnameRequest) => {
		const data = await $fetch<CompanyCheckCnameResponse>(CompanyCheckCname.url(), {
			...useFetchConfig(),
			method: CompanyCheckCname.method,
			body: formData
		})
		return data.data
	}

	const getCompany = async (id: number) => {
		const data = await $fetch<GetCompanyResponse>(GetCompany.url(id), {
			...useFetchConfig(),
			method: GetCompany.method
		})
		return data.data
	}

	const organizationChart = async (params: OrganizationChartRequest) => {
		const data = await $fetch<OrganizationChartResponse>(OrganizationChart.url(), {
			...useFetchConfig(),
			method: OrganizationChart.method,
			params
		})
		return data.data
	}

	return {
		...state,
		domainCompany,
		fetchCompany,
		createCompany,
		editCompany,
		deleteCompany,
		getAllCompanies,
		searchCompanies,
		getCompany,
		setCompany,
		checkCName,
		organizationChart
	}
})

// company :: create company
export interface CreateCompanyRequest {
	title?: string
	description?: string
	color?: string
	image_id?: number | null
	timezone?: string
	// payroll
	payroll_homepage?: string
	payroll_baseurl?: string
	is_change_payroll_domain?: boolean
	// career
	career_homepage?: string
	career_baseurl?: string
	is_change_career_domain?: boolean
}
export interface CreateCompanyResponse extends ApiResponse {
	data: Company
}
const CreateCompany = {
	method: "POST" as const,
	url: () => `companies/create`
}

// company :: edit company
export interface EditCompanyRequest {
	id: number
	title?: string
	description?: string
	color?: string
	image_id?: number | null
	timezone?: string
	notify_payroll?: boolean
	notify_performance_review?: boolean
	notify_registration?: boolean
	notify_birthday?: boolean
	// payroll
	payroll_homepage?: string
	payroll_baseurl?: string
	is_change_payroll_domain?: boolean
	// career
	career_homepage?: string
	career_baseurl?: string
	is_change_career_domain?: boolean
}
export interface EditCompanyResponse extends ApiResponse {
	data: Company
}
const EditCompany = {
	method: "POST" as const,
	url: () => `companies/edit`
}

// company :: delete company
export interface DeleteCompanyRequest {}
export interface DeleteCompanyResponse extends ApiResponse {}
const DeleteCompany = {
	method: "POST" as const,
	url: (id: any) => `companies/delete/${id}`
}

// company :: get all companies
export interface GetAllCompaniesRequest {
	user_id?: number
}
export interface GetAllCompaniesResponse extends ApiResponse {
	data: Company[]
}
const GetAllCompanies = {
	method: "GET" as const,
	url: () => `companies/all`
}

// company :: search companies
export interface SearchCompaniesRequest {
	search?: string
	user?: string
	limit?: number
	page?: number
	order_by?: string
	direction?: string
	user_id?: number
}
export interface SearchCompaniesResponse extends ApiResponse {
	data: {
		companies: Company[]
		total: number
	}
}
const SearchCompanies = {
	method: "GET" as const,
	url: () => `companies/search`
}

// company :: check cname
export interface CompanyCheckCnameRequest {
	domain: string
	target: string
}
export interface CompanyCheckCnameResponse extends ApiResponse {
	data: {
		domain: string
		target: string
		ok: boolean
	}
}
const CompanyCheckCname = {
	method: "POST" as const,
	url: () => `companies/check-cname`
}

// company :: get company
export interface GetCompanyRequest {}
export interface GetCompanyResponse extends ApiResponse {
	data: Company
}
const GetCompany = {
	method: "GET" as const,
	url: (id: any) => `companies/${id}`
}

// company :: organization chart
export interface OrganizationChartRequest {}
export interface OrganizationChartResponse extends ApiResponse {
	data: OrganizationChartNode[]
}
const OrganizationChart = {
	method: "GET" as const,
	url: () => `companies/organization-chart`
}

export const useCompanyApiUrl = () => ({
	CreateCompany,
	EditCompany,
	DeleteCompany,
	GetAllCompanies,
	GetCompany,
	OrganizationChart
})
